import { CheckCircleFilled } from '@ant-design/icons';
import { App as AntApp, Button, Divider, Empty, Flex, Modal, Typography, theme } from 'antd';
import { useMyPoints } from '../../../points';
import { selectCartTotal, useCart } from '../../cart-store';
import { useCheckout } from '../../hooks';
import { formatPoints } from '../../utils';
import { CartItemRow } from '../CartItemRow/CartItemRow';

const { Text, Title } = Typography;

type Props = {
  open: boolean;
  onClose: () => void;
};

export const CartModal = ({ open, onClose }: Props) => {
  const items = useCart((s) => s.items);
  const total = useCart(selectCartTotal);
  const { data } = useMyPoints();
  const balance = data?.balance ?? 0;
  const checkout = useCheckout();
  const { message, modal } = AntApp.useApp();
  const { token } = theme.useToken();

  const insufficient = total > balance;

  const showOrderConfirmation = () =>
    modal.success({
      title: 'Order created',
      icon: <CheckCircleFilled style={{ color: token.colorPrimary }} />,
      content: <Text type="secondary">A moderator will reach out to you as soon as possible</Text>,
      okText: 'Done',
      centered: true,
    });

  const onCheckout = () => {
    checkout.mutate(undefined, {
      onSuccess: () => {
        onClose();
        showOrderConfirmation();
      },
      onError: (err) => message.error(err.message),
    });
  };

  return (
    <Modal title="Your cart" open={open} onCancel={onClose} footer={null} width={440}>
      {items.length === 0 ? (
        <Empty description="Your cart is empty" />
      ) : (
        <Flex vertical gap={4}>
          {items.map((item) => (
            <CartItemRow key={item.skuId} item={item} />
          ))}
          <Divider style={{ margin: '12px 0' }} />
          <Flex justify="space-between" align="center">
            <Text>Total</Text>
            <Title level={5} style={{ margin: 0 }}>
              {formatPoints(total)}
            </Title>
          </Flex>
          <Flex justify="space-between" align="center">
            <Text type="secondary">Your balance</Text>
            <Text type={insufficient ? 'danger' : 'secondary'}>{formatPoints(balance)}</Text>
          </Flex>
          <Button
            type="primary"
            size="large"
            block
            style={{ marginTop: 12 }}
            loading={checkout.isPending}
            disabled={insufficient}
            onClick={onCheckout}
          >
            {insufficient ? `Need ${formatPoints(total - balance)} more` : 'Checkout'}
          </Button>
        </Flex>
      )}
    </Modal>
  );
};
